/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import Chart from "react-apexcharts";
import { useUserStats } from "../../store/useUserStats";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";

dayjs.extend(isoWeek);

const monthOptions = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const quarterOptions = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

export const RegidsteredUsersChart = React.memo(() => {
  const snapshotRef = useRef(null);
  const {
    registeredUserStats,
    getRegisteredUserStats,
    isRegisteredUserStatsLoading,
  } = useUserStats();
  const [_, takeScreenshot] = useScreenshot();
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);

  const [fromToPeriod, setFromToPeriod] = useState<
    "month" | "week" | "quarter" | "year"
  >("month");
  const [startYear, setStartYear] = useState(() =>
    dayjs().subtract(12, "month").year()
  );
  const [startMonth, setStartMonth] = useState(
    () => dayjs().subtract(12, "month").month() + 1
  );
  const [endYear, setEndYear] = useState(() => dayjs().year());
  const [endMonth, setEndMonth] = useState(() => dayjs().month() + 1);
  const [startWeek, setStartWeek] = useState(() =>
    dayjs().subtract(12, "month").isoWeek()
  );
  const [startQuarter, setStartQuarter] = useState(1);
  const [endWeek, setEndWeek] = useState(() => dayjs().isoWeek());
  const [endQuarter, setEndQuarter] = useState(4);

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );
  const startMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return startYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [startYear]);
  const endMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return endYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [endYear]);
  const getWeekOptions = (y: number) => {
    const lastDay = dayjs(`${y}-12-31`);
    const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
    const currentWeek = dayjs().isoWeek();
    const currentY = dayjs().year();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const start = dayjs().year(y).isoWeek(w).startOf("isoWeek");
      const end = dayjs().year(y).isoWeek(w).endOf("isoWeek");
      return {
        value: w,
        label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
        disabled: y === currentY && w > currentWeek,
      };
    });
  };
  const startWeekOptions = useMemo(
    () => getWeekOptions(startYear),
    [startYear]
  );
  const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "registered_users.png";
      link.click();
    });
  };

  const downloadCsv = () => {
    if (!hasFromToData) return;
    setIsCsvDownloading(true);
    const buildDailyParams = () => {
      if (fromToPeriod === "week") {
        const startDate = dayjs()
          .year(startYear)
          .isoWeek(startWeek)
          .startOf("isoWeek");
        const endDate = dayjs()
          .year(endYear)
          .isoWeek(endWeek)
          .endOf("isoWeek");
        return {
          params: {
            startYear: startDate.year(),
            startMonth: startDate.month() + 1,
            endYear: endDate.year(),
            endMonth: endDate.month() + 1,
            period: "day",
          },
        };
      }

      return {
        params: {
          startYear,
          startMonth:
            fromToPeriod === "year"
              ? 1
              : fromToPeriod === "quarter"
                ? (startQuarter - 1) * 3 + 1
                : startMonth,
          endYear,
          endMonth:
            fromToPeriod === "year"
              ? 12
              : fromToPeriod === "quarter"
                ? endQuarter * 3
                : endMonth,
          period: "day",
        },
      };
    };

    axios
      .get("/users/registered-stats", {
        params: buildDailyParams().params,
      })
      .then((response) => {
        const categories = (response.data?.categories as string[]) ?? [];
        const data = (response.data?.data as number[]) ?? [];
        const rows = categories.map((label, idx) => `${label},${data[idx] ?? ""}`);
        const csv = "Date,Users registered\n" + rows.join("\n");
        const blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `registered_users_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading CSV for RegisteredUsers:", error);
      })
      .finally(() => {
        setIsCsvDownloading(false);
      });
  };

  useEffect(() => {
    let apiStartYear: number;
    let apiStartMonth: number;
    let apiEndYear: number;
    let apiEndMonth: number;
    if (fromToPeriod === "week") {
      const startDate = dayjs()
        .year(startYear)
        .isoWeek(startWeek)
        .startOf("isoWeek");
      const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
      apiStartYear = startDate.year();
      apiStartMonth = startDate.month() + 1;
      apiEndYear = endDate.year();
      apiEndMonth = endDate.month() + 1;
    } else if (fromToPeriod === "quarter") {
      apiStartYear = startYear;
      apiStartMonth = (startQuarter - 1) * 3 + 1;
      apiEndYear = endYear;
      apiEndMonth = endQuarter * 3;
    } else if (fromToPeriod === "year") {
      apiStartYear = startYear;
      apiStartMonth = 1;
      apiEndYear = endYear;
      apiEndMonth = 12;
    } else {
      apiStartYear = startYear;
      apiStartMonth = startMonth;
      apiEndYear = endYear;
      apiEndMonth = endMonth;
    }
    getRegisteredUserStats(undefined, undefined, {
      period: fromToPeriod,
      startYear: apiStartYear,
      startMonth: apiStartMonth,
      endYear: apiEndYear,
      endMonth: apiEndMonth,
    });
  }, [
    fromToPeriod,
    startYear,
    startMonth,
    startWeek,
    startQuarter,
    endYear,
    endMonth,
    endWeek,
    endQuarter,
    getRegisteredUserStats,
  ]);

  interface FromToResponse {
    categories: string[];
    data: number[];
  }
  const isFromToResponse = (s: unknown): s is FromToResponse =>
    s !== null &&
    typeof s === "object" &&
    "categories" in s &&
    Array.isArray((s as FromToResponse).categories) &&
    "data" in s &&
    Array.isArray((s as FromToResponse).data);

  const hasFromToData =
    registeredUserStats &&
    isFromToResponse(registeredUserStats) &&
    registeredUserStats.categories.length > 0;

  const fromToCategories = hasFromToData ? registeredUserStats.categories : [];
  const fromToData = hasFromToData ? registeredUserStats.data : [];

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white border-gray-border rounded-xl shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div></div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
          <div className="flex items-center gap-1 sm:gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {fromToPeriod === "month"
                    ? "Monthly"
                    : fromToPeriod === "week"
                      ? "Weekly"
                      : fromToPeriod === "quarter"
                        ? "Quarterly"
                        : "Yearly"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {(["month", "week", "quarter", "year"] as const).map((p) => (
                  <MenuItem
                    key={p}
                    disabled={fromToPeriod === p}
                    onClick={() => setFromToPeriod(p)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {p === "month"
                      ? "Monthly"
                      : p === "week"
                        ? "Weekly"
                        : p === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>
          <div className="flex items-center gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={startYear === y}
                    onClick={() => setStartYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {startMonthOptions.find((m) => m.value === startMonth)
                      ?.label || "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {startMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={startMonth === m.value}
                      onClick={() => setStartMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      startWeekOptions.find((w) => w.value === startWeek)?.label
                    }
                  >
                    {startWeekOptions.find((w) => w.value === startWeek)
                      ?.label || "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {startWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={startWeek === w.value}
                      onClick={() => setStartWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {
                      quarterOptions.find((q) => q.value === startQuarter)
                        ?.label
                    }
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={startQuarter === q.value}
                      onClick={() => setStartQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">To:</label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={endYear === y}
                    onClick={() => setEndYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {endMonthOptions.find((m) => m.value === endMonth)?.label ||
                      "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {endMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={endMonth === m.value}
                      onClick={() => setEndMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      endWeekOptions.find((w) => w.value === endWeek)?.label
                    }
                  >
                    {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                      "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {endWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={endWeek === w.value}
                      onClick={() => setEndWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {quarterOptions.find((q) => q.value === endQuarter)?.label}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={endQuarter === q.value}
                      onClick={() => setEndQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </div>

          <PrimaryButton
            size="sm"
            onClick={downloadCsv}
            disabled={!hasFromToData || isCsvDownloading}
            label={isCsvDownloading ? "..." : "Download CSV"}
            className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
            className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
        </div>
      </div>
      <div ref={snapshotRef} className="mt-4">
        <div className="flex items-center mb-2 sm:mb-12">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            New downloads per period
          </p>
          <Tooltip
            content="Show all new downloads per week/month/quarter/year. Default: month. Non-cumulative."
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        {isRegisteredUserStatsLoading || registeredUserStats === null ? (
          <div className="flex items-center justify-center h-[240px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : hasFromToData ? (
          <Chart
            {...chartConfig}
            options={{
              ...chartConfig?.options,
              xaxis: {
                ...chartConfig?.options?.xaxis,
                categories: fromToCategories,
              },
            }}
            series={[
              {
                name: "Users registered",
                data: fromToData,
              },
            ]}
          />
        ) : (
          <div className="w-full h-[240px] flex items-center justify-center">
            <p className="font-satoshi font- text-2xl text-tailgrids-black">
              No data found
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

const chartConfig = {
  type: "bar" as const,
  width: "100%",
  height: 240,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    title: {
      text: "",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#194128", "#377D51"],
    plotOptions: {
      bar: {
        columnWidth: 12,
        borderRadius: 2,
      },
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#374151",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#374151",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 0,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      theme: "dark",
    },
  },
};
